import React from 'react';

import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

/**
 * The Luxury Escapes loyalty "coin": a circular tier-coloured badge with the
 * "le" monogram embossed in the centre.
 *
 * Ported from www-le-customer's `LoyaltyTierIcon` / `LineLuxuryEscapesMonogramIcon`.
 * The monogram is rendered as a raw inline SVG (rather than MUI `SvgIcon`) so it
 * stays self-coloured via `currentColor` and is not overridden by ancestor
 * `& .MuiSvgIcon-root` colour rules.
 */

/** Fraction of the coin diameter occupied by the monogram (mirrors the customer scale(0.66)). */
const MONOGRAM_SCALE = '66%';

interface Props {
  /** Loyalty tier whose gradient / contrast colours the coin adopts. */
  tier: App.LuxLoyaltyTier;
  /** Diameter of the coin in pixels. Defaults to 24. */
  size?: number;
  className?: string;
  sx?: SxProps<Theme>;
}

function LoyaltyCoinIcon({ tier, size = 24, className, sx }: Props) {
  return (
    <Box
      className={className}
      aria-hidden="true"
      sx={[
        (theme) => {
          const palette = theme.palette.loyaltyTiers[tier];
          return {
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            height: size,
            width: size,
            borderRadius: '50%',
            background: palette.gradient,
            color: palette.contrastText,
            boxShadow: `inset 0 0 0 1px ${palette.main}`,
          };
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <svg width={MONOGRAM_SCALE} height={MONOGRAM_SCALE} viewBox="0 0 24 24" fill="none" focusable="false">
        <path
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M16.226 5.511a2.1612 2.1612 0 0 0-.965.0692c-1.3693.5442-2.2208 2.2034-2.7343 3.5237-.6057 1.5344-1.0797 3.3141-.9217 4.9778.1667 1.7083 1.0182 3.7021 2.9801 3.6397.794-.0169 1.5567-.3177 2.154-.8497.5973-.5319.9908-1.2608 1.1115-2.0585.0922-.7047-.0702-.7939-.7549-1.1017-.2678-.116-.7681-.174-.7681.2408 0 .1014.0454.1974.0889.2892.0254.0537.0501.1059.0647.1569.0407.1457.0671.2951.079.446.1317 1.3292-.8602 2.5692-2.4315 2.1901-.6847-.1695-.8954-1.0259-.9744-1.637a2.812 2.812 0 0 1-.0263-1.2088c.0643-.1338.4555-.2285.7666-.3039.1348-.0326.2546-.0616.3262-.0886 1.4252-.5257 2.7016-1.3987 3.7176-2.5424.9173-1.0839 2.0014-2.6762.9656-4.0144a5.6553 5.6553 0 0 0-1.7776-1.3693 2.1743 2.1743 0 0 0-.9004-.3591ZM7.0184 21.8963a3.3874 3.3874 0 0 1 1.4791-.1204 8.4913 8.4913 0 0 1 2.7388.7582 3.8381 3.8381 0 0 0 1.496.4512 3.8262 3.8262 0 0 0 1.55-.1835c.4505-.1641.8295-.4844 1.0704-.9046a2.0937 2.0937 0 0 0 .2463-1.3925c-.1226-.6432-.6879-.3076-1.1465-.0355-.1489.0884-.2865.1701-.3941.2094a7.0075 7.0075 0 0 1-2.2428.4193c-.5728.0177-1.1491-.0209-1.7255-.0594-1.0223-.0684-2.045-.1368-3.0498.1085a20.5088 20.5088 0 0 0 1.0051-2.5915c.3424-.9233.6233-1.8645.8778-2.819.4872-1.8555.9612-3.7289 1.4002-5.5978a328.3597 328.3597 0 0 1 1.3825-5.629 13.6887 13.6887 0 0 1 .7637-2.3952 6.1092 6.1092 0 0 1 .0245-.0545c.1115-.2468.3178-.7036.1511-.9268-.1771-.237-.7023-.0984-.945-.0344l-.0118.0031a2.4099 2.4099 0 0 0-.9791.5287 2.462 2.462 0 0 0-.6449.9165c-.215.397-.632 1.3203-.654 1.4273-.0512.0605-.3249.2828-.6992.5869-.813.6603-2.1006 1.706-2.6145 2.3169l-.0446.053c-.8592 1.0199-2.2029 2.615-.7147 3.9301.4301.388 1.5274.8162 1.7863.446a.772.772 0 0 0 .1418-.551.766.766 0 0 0-.2734-.4972c-.2327-.2364.0175-2.2168.5486-3.0866a14.8603 14.8603 0 0 1 1.8522-2.132S7.7426 13.5732 6.8297 17.815a22.599 22.599 0 0 1-.79 3.0776c-.0251.0623-.0515.1295-.0787.1987-.0584.1488-.1204.3066-.1803.4436-.0413.0943-.099.1768-.1558.2581-.0642.0918-.1274.182-.1646.2861-.1536.4326.18.6334.5574.4728.1795-.0764.3253-.1961.4713-.3161.161-.1322.3223-.2647.5294-.3396ZM17.3899 6.9452c.5662.7582-.1141 2.1499-.4872 2.8368-.7417 1.5923-2.0058 3.033-3.7044 3.5371.3235-1.9774 1.1243-3.8424 2.3306-5.4283.3029-.3925 1.3562-1.6192 1.861-.9456Z"
        />
      </svg>
    </Box>
  );
}

export default LoyaltyCoinIcon;
